/**
 * High-frequency dex live-scan debug-telemetry gate + capture buffer (R19,
 * 2026-06-26; capture buffer added 2026-07-14).
 *
 * WHY THIS EXISTS. A full live scan emits tens of thousands of `[dex-scan]`
 * console.log lines. On device each line crosses the JS->native console bridge;
 * the sheer volume back-pressures the bridge queue and, as a scan runs longer,
 * starves the frame loop so tail-end frames are dropped/delayed AND the Xcode
 * debug console itself stalls mid-scan (the "telemetry froze" symptom). The scan
 * ENGINE is healthy — the clog is purely console-bridge I/O pressure.
 *
 * THE FIX (capture buffer): a console.log TEE intercepts every `[dex-scan]` /
 * `[live-scan]` line, appends it to a bounded in-memory ring buffer, and does
 * NOT forward it to the console bridge. So the telemetry is captured in full
 * (export it to a file via the Settings "Export scan log" button) while the
 * bridge stays quiet — no freeze, no frame-loop starvation. Non-scan console
 * logs pass through untouched.
 *
 * INVARIANT — logging changes ONLY whether/where lines are recorded. It NEVER
 * touches any scan / classify / write decision path; capture ON vs OFF must
 * produce byte-identical dex writes.
 */

/**
 * Master verbosity flag for high-frequency dex live-scan debug telemetry. When
 * true the per-frame / per-cell markers are GENERATED (and captured by the tee
 * into the ring buffer). The tee keeps them off the console bridge, but even
 * captured they cost real per-frame CPU on device — building each payload object
 * and JSON-serialising it into the ring buffer, ~50×/sec during a scan. That
 * work is pure heat when nobody is reading a trace.
 *
 * 2026-07-29: set to FALSE to measure live-scan thermals on a clean build (the
 * per-frame `if (DEX_SCAN_VERBOSE) console.log(...)` sites and the
 * `dexScanVerboseLog` thunks all no-op). Flip back to `true` when you need a
 * device trace via Settings → "Export scan log". (A future refinement could gate
 * this on `import.meta.env.DEV`, but that would leave release/TestFlight builds
 * unable to export a scan trace, which is still occasionally wanted — so it stays
 * a manual switch.)
 */
export const DEX_SCAN_VERBOSE = false;

/**
 * Gated emitter for high-frequency dex-scan debug telemetry. A no-op unless
 * verbose logging is enabled. `fields` is a LAZY THUNK so the payload is NOT
 * constructed when the flag is off. The thunk must be side-effect-free.
 */
export function dexScanVerboseLog(
  event: string,
  fields?: () => Record<string, unknown>,
  enabled: boolean = DEX_SCAN_VERBOSE,
): void {
  if (!enabled) return;
  // eslint-disable-next-line no-console
  if (fields === undefined) console.log(`[dex-scan] ${event}`);
  else console.log(`[dex-scan] ${event}`, fields());
}

// ── Capture buffer ──────────────────────────────────────────────────────────

/**
 * Max lines held in the ring buffer. A busy scan emits ~50 lines/sec, so 80k
 * lines covers a ~25-minute multi-region session. At ~150 bytes/line that caps
 * memory near ~12MB; when full the OLDEST lines are dropped (a scan's tail is
 * usually what matters). Kept in memory (not streamed to disk) to avoid native
 * changes — a native file-append sink is a possible future upgrade.
 */
const CAPTURE_MAX_LINES = 80_000;

let captureBuffer: string[] = [];
let teeInstalled = false;
let teeInstalledAt = 0;
/** The pre-tee console.log (Capacitor's bridge-forwarding one once installed). */
let originalConsoleLog: ((...args: unknown[]) => void) | null = null;

/** Prefixes whose console.log lines are captured and kept off the bridge. */
// "[auth]" rides along so a sign-out at launch shows up in the same export
// as the scan it interrupted (2026-10-06 Galaxy).
const CAPTURED_PREFIXES = ["[dex-scan]", "[live-scan]", "[auth]"];

function serializeLogArgs(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === "string") return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(" ");
}

function isCapturedLine(args: unknown[]): boolean {
  const first = args[0];
  return typeof first === "string" && CAPTURED_PREFIXES.some((p) => first.startsWith(p));
}

/**
 * Install the console.log tee. Idempotent. Call once from the scan entry point
 * AFTER Capacitor has patched console (so non-captured lines still reach the
 * native bridge). Captured scan lines go to the ring buffer only.
 */
export function installDexScanLogTee(): void {
  if (teeInstalled) return;
  teeInstalled = true;
  teeInstalledAt = Date.now();
  const original = console.log.bind(console) as (...args: unknown[]) => void;
  originalConsoleLog = original;
  console.log = (...args: unknown[]) => {
    if (isCapturedLine(args)) {
      // Every exported line carries the wall-clock ms since the tee went in
      // (2026-09-25 Galaxy stall: gaps of 68 s / 112 s could not be placed
      // in time, so a frozen WebView and a still screen read the same).
      pushCaptured(`+${Date.now() - teeInstalledAt} ${serializeLogArgs(args)}`);
      return;
    }
    original(...args);
  };
}

function pushCaptured(line: string): void {
  captureBuffer.push(line);
  if (captureBuffer.length > CAPTURE_MAX_LINES) {
    // Drop oldest in a batch so we don't shift() on every hot-path line.
    captureBuffer.splice(0, captureBuffer.length - CAPTURE_MAX_LINES);
  }
}

/**
 * Append one line to the capture buffer from outside the tee: the few lines
 * a device report needs in the export whether or not a scan has run (the
 * store rating gate, lib/ratingLog). Stamped like the tee's own lines once
 * the tee is installed, and with the wall clock before that, so they can
 * still be placed in time. The caller forwards the line to the console
 * itself: the buffer is the export, not a sink, and a line that is not a
 * captured prefix passes the tee untouched.
 */
export function appendDexScanCaptureLine(text: string): void {
  const stamp = teeInstalled ? `+${Date.now() - teeInstalledAt}` : `@${new Date().toISOString()}`;
  pushCaptured(`${stamp} ${text}`);
}


/** Number of buffered telemetry lines. */
export function dexScanCaptureLineCount(): number {
  return captureBuffer.length;
}

/** The full captured telemetry as newline-joined text (for file export). */
export function getDexScanCaptureText(): string {
  return captureBuffer.join("\n");
}

/** Clear the ring buffer (e.g. before starting a fresh trace). */
export function clearDexScanCapture(): void {
  captureBuffer = [];
}

/**
 * Test-only: remove the tee and restore the original console.log so suites do
 * not leak a patched console across files.
 */
export function __uninstallDexScanLogTeeForTests(): void {
  if (originalConsoleLog) console.log = originalConsoleLog;
  originalConsoleLog = null;
  teeInstalled = false;
  captureBuffer = [];
}
